import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { transcribeAudio } from '../../services/orbitVoiceApi';

/**
 * OrbitVoice — hold-to-talk microphone button.
 *
 * Press and hold (or click-to-toggle): records audio via MediaRecorder,
 * streams live amplitude into a waveform, releases into a Whisper transcribe
 * call, and hands the transcript back via `onTranscript`.
 *
 * Edge cases handled:
 *   - Browser without MediaRecorder / getUserMedia: renders nothing (caller decides fallback).
 *   - Mic permission denied: surfaces `onError` with user-readable string.
 *   - Very short presses (<400ms): discards recording silently to avoid empty transcripts.
 *   - Tab hidden while recording: auto-stops + submits to prevent ghost streams.
 */

export interface OrbitVoiceProps {
  onTranscript: (text: string) => void;
  onError?: (message: string) => void;
  onRecordingChange?: (recording: boolean) => void;
  // Context hint passed to Whisper to bias vocabulary (brand/product terms).
  promptHint?: string;
  languageHint?: string;
  // When true, auto-submits the transcript on release (no confirmation).
  autoSubmit?: boolean;
  // Visual size. "md" (default) fits chat inputs; "lg" is the hero pill.
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

type State = 'idle' | 'recording' | 'transcribing' | 'unsupported' | 'denied';

const SIZE_MAP = {
  sm: { pad: 'p-2', icon: 14 },
  md: { pad: 'p-2.5', icon: 18 },
  lg: { pad: 'p-3.5', icon: 22 },
} as const;

const MIN_RECORDING_MS = 400;

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return 'audio/webm';
}

const OrbitVoice: React.FC<OrbitVoiceProps> = ({
  onTranscript,
  onError,
  onRecordingChange,
  promptHint,
  languageHint,
  autoSubmit = true,
  size = 'md',
  className = '',
  disabled = false,
}) => {
  const [state, setState] = useState<State>('idle');
  const [amplitude, setAmplitude] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const stateRef = useRef<State>('idle');

  useEffect(() => { stateRef.current = state; }, [state]);

  // Feature detection on mount.
  useEffect(() => {
    const ok =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined';
    if (!ok) setState('unsupported');
  }, []);

  // Clean up on unmount.
  useEffect(() => () => { cleanupStream(); }, []);

  const cleanupStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { /* noop */ });
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, []);

  const runAmplitudeLoop = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      // Compute RMS amplitude in [0,1].
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      setAmplitude(Math.min(1, rms * 2.2)); // boost floor so quiet speech shows
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const startRecording = useCallback(async () => {
    if (stateRef.current !== 'idle' || disabled) return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Microphone access denied. Enable it in your browser settings.'
        : 'Could not start microphone.';
      setState('denied');
      onError?.(msg);
      // Back to idle after a moment so user can retry.
      setTimeout(() => setState('idle'), 2500);
      return;
    }

    streamRef.current = stream;
    const mime = pickMimeType();
    const recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 96_000 });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = handleRecorderStop;
    mediaRecorderRef.current = recorder;

    // Amplitude analyser.
    try {
      const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.75;
      src.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      runAmplitudeLoop();
    } catch { /* analyser is non-critical */ }

    startedAtRef.current = Date.now();
    setState('recording');
    onRecordingChange?.(true);
    recorder.start(250);
  }, [disabled, onError, onRecordingChange, runAmplitudeLoop]);

  const handleRecorderStop = useCallback(async () => {
    onRecordingChange?.(false);
    const elapsed = Date.now() - startedAtRef.current;
    const chunks = chunksRef.current;
    chunksRef.current = [];
    cleanupStream();
    setAmplitude(0);

    if (elapsed < MIN_RECORDING_MS || chunks.length === 0) {
      setState('idle');
      return;
    }

    setState('transcribing');
    const mime = chunks[0].type || 'audio/webm';
    const blob = new Blob(chunks, { type: mime });

    try {
      const result = await transcribeAudio(blob, {
        language: languageHint,
        prompt: promptHint,
      });
      setState('idle');
      if (result.text && autoSubmit) {
        onTranscript(result.text);
      } else if (result.text) {
        onTranscript(result.text);
      }
    } catch (err: any) {
      setState('idle');
      onError?.(err?.message || 'Transcription failed.');
    }
  }, [autoSubmit, cleanupStream, languageHint, onError, onRecordingChange, onTranscript, promptHint]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop(); } catch { /* noop */ }
    }
  }, []);

  // Stop if the tab becomes hidden (prevents ghost mic streams).
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && stateRef.current === 'recording') stopRecording();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [stopRecording]);

  // ── Render ────────────────────────────────────────────────────────

  if (state === 'unsupported') return null;

  const sz = SIZE_MAP[size];
  const recording = state === 'recording';
  const transcribing = state === 'transcribing';
  const denied = state === 'denied';

  const handleClick = () => {
    if (recording) stopRecording();
    else if (state === 'idle') startRecording();
  };

  // Waveform ring intensity driven by live amplitude (0..1).
  const ringScale = 1 + amplitude * 0.45;
  const ringOpacity = 0.25 + amplitude * 0.55;

  return (
    <button
      type="button"
      aria-label={recording ? 'Stop recording' : 'Start voice input'}
      onClick={handleClick}
      disabled={disabled || transcribing}
      className={`relative inline-flex items-center justify-center rounded-full transition-all duration-200 ${sz.pad} ${className}`}
      style={{
        background: recording
          ? 'rgba(239, 68, 68, 0.10)'
          : denied
            ? 'rgba(239, 68, 68, 0.06)'
            : 'rgba(17, 24, 39, 0.04)',
        border: `1px solid ${recording ? 'rgba(239,68,68,0.35)' : 'rgba(17,24,39,0.08)'}`,
        color: recording ? '#DC2626' : denied ? '#DC2626' : '#374151',
        cursor: disabled || transcribing ? 'not-allowed' : 'pointer',
      }}
    >
      {/* Live waveform ring — only visible while recording */}
      {recording && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            transform: `scale(${ringScale})`,
            opacity: ringOpacity,
            background: 'radial-gradient(circle, rgba(239,68,68,0.28) 0%, transparent 70%)',
            transition: 'transform 80ms linear, opacity 80ms linear',
          }}
        />
      )}

      {transcribing ? (
        <Loader2 size={sz.icon} className="animate-spin" />
      ) : recording ? (
        <Square size={sz.icon} fill="currentColor" strokeWidth={0} />
      ) : (
        <Mic size={sz.icon} />
      )}
    </button>
  );
};

export default OrbitVoice;
